import type {
  TaskList,
  CreateTaskListInput,
  UpdateTaskListInput,
  DeleteTaskListAction,
} from '@/types/task-lists';
import { generateAPIUrl } from '@/utils';

type ListsResponse = { lists: TaskList[] };
type ListResponse = { list: TaskList };

export class TaskListsApiClient {
  private accessToken: string;

  constructor(accessToken: string) {
    this.accessToken = accessToken;
  }

  private get headers(): HeadersInit {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.accessToken}`,
    };
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(generateAPIUrl(path), {
      ...options,
      headers: { ...this.headers, ...(options.headers as Record<string, string>) },
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        (body as { error?: string }).error ?? `Request failed: ${response.status}`,
      );
    }
    return response.json() as Promise<T>;
  }

  async listLists(): Promise<TaskList[]> {
    const data = await this.request<ListsResponse>('/api/task-lists');
    return data.lists;
  }

  async createList(input: CreateTaskListInput): Promise<TaskList> {
    const data = await this.request<ListResponse>('/api/task-lists', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return data.list;
  }

  async updateList(listId: string, input: UpdateTaskListInput): Promise<TaskList> {
    const data = await this.request<ListResponse>(`/api/task-lists/${listId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    return data.list;
  }

  async deleteList(listId: string, action: DeleteTaskListAction): Promise<void> {
    await this.request<{ success: boolean }>(`/api/task-lists/${listId}`, {
      method: 'DELETE',
      body: JSON.stringify(action),
    });
  }

  async reorderLists(orderedIds: string[]): Promise<void> {
    await this.request<{ success: boolean }>('/api/task-lists/reorder', {
      method: 'POST',
      body: JSON.stringify({ orderedIds }),
    });
  }
}
